import { useFormContext, Controller, useWatch } from 'react-hook-form';
import { Box, FormControl, InputAdornment, TextField } from '@mui/material';
import GlobalLineIcon from 'remixicon-react/GlobalLineIcon';
import FileUpload from './FileUpload';
import { WriteWithAITabName } from '../../hooks/useWriteWithAI';
import { WriteWithAIFormValues } from '../../hooks/useWriteWithAIForm';
import TextEditor from './TextEditor';
import { useMemo } from 'react';
import { AnswerContainer } from '@buildwise/pages/buildwiseAdmin/BuildWiseGPT/PromptHistory';
import RenderLLMMarkdown from '@buildwise/pages/buildwiseAdmin/BuildWiseGPT/RenderLLMMarkdown';
import { AttachmentType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { isEmpty } from 'lodash';
import RenderAttachments from './RenderAttachments';
import WriteWithAISkeleton from './WriteWithAISkeleton';
import HandleOperation from 'components/utils/HandleOperation';
import SelectFilesSkeleton from '../ParseFile/SelectFilesSkeleton';

type WriteWithAIFormProps = {
  tab?: WriteWithAITabName;
};

function WriteWithAIForm({ tab = WriteWithAITabName.TEXT }: WriteWithAIFormProps) {
  const {
    control,
    formState: { isSubmitting },
  } = useFormContext<WriteWithAIFormValues>();
  const text = useWatch({
    control,
    name: 'text',
    defaultValue: [],
  });
  const lastPrompt = useMemo(() => text[text.length - 1], [text]);

  return (
    <Box>
      {!lastPrompt.genAIResponse && !lastPrompt.loading && (
        <>
          {tab === WriteWithAITabName.TEXT && (
            <>
              <TextEditor />
            </>
          )}
          {tab === WriteWithAITabName.URL && (
            <Controller
              control={control}
              name="url"
              render={({ field, fieldState }) => (
                <FormControl fullWidth>
                  <TextField
                    {...field}
                    error={!!fieldState.error}
                    helperText={fieldState?.error?.message || ''}
                    fullWidth
                    placeholder="Enter your URL here"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <GlobalLineIcon />
                        </InputAdornment>
                      ),
                      sx: {
                        '& .MuiInputBase-input': {
                          padding: '14px 0px',
                        },
                      },
                    }}
                    disabled={isSubmitting}
                  />
                </FormControl>
              )}
            />
          )}
          {tab === WriteWithAITabName.URL && <FileUpload />}
        </>
      )}
      {(lastPrompt?.loading || lastPrompt?.genAIResponse) && (
        <AnswerContainer
          width="100% !important"
          padding="16px !important"
          sx={{ borderRadius: '8px', mr: 0 }}
        >
          {lastPrompt?.loading ? (
            <WriteWithAISkeleton />
          ) : (
            <>
              <RenderLLMMarkdown llmResponse={lastPrompt?.genAIResponse || ''} />
              <HandleOperation
                isLoading={lastPrompt.isLoadingFiles}
                data={lastPrompt.embeddedAttachments}
                skelton={<SelectFilesSkeleton />}
                skeltonCount={1}
              >
                {!isEmpty(lastPrompt.embeddedAttachments) && (
                  <RenderAttachments
                    attachments={lastPrompt.embeddedAttachments as AttachmentType[]}
                  />
                )}
              </HandleOperation>
            </>
          )}
        </AnswerContainer>
      )}
    </Box>
  );
}

export default WriteWithAIForm;
